import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Radio, AlertOctagon, Phone, CheckCircle2, MapPin, Users2, Wifi, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api, BACKEND_URL } from "@/lib/api";
import LiveMap from "@/components/LiveMap";
import { useAuth } from "@/context/AuthContext";

const CAT_COLOR = {
  medical: "destructive",
  fire: "destructive",
  accident: "accent",
  harassment: "primary",
  general: "secondary",
};

export default function ResponderPortal() {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [wsStatus, setWsStatus] = useState("connecting");
  const wsRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/sos/active");
      setAlerts(data);
    } catch (e) {
      toast.error("Failed to load active alerts");
    }
  };

  useEffect(() => {
    load();

    // WebSocket connection for real-time broadcasts
    const token = localStorage.getItem("g360_token");
    const wsProto = BACKEND_URL.startsWith("https") ? "wss" : "ws";
    const wsUrl = `${BACKEND_URL.replace(/^https?/, wsProto)}/api/ws/sos?token=${encodeURIComponent(token)}`;

    let closed = false;
    const connect = () => {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => setWsStatus("live");
      ws.onclose = () => {
        setWsStatus("offline");
        if (!closed) setTimeout(connect, 3000);
      };
      ws.onerror = () => setWsStatus("offline");
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "sos_new") {
            setAlerts((prev) => [msg.alert, ...prev.filter((a) => a.id !== msg.alert.id)]);
            toast.error(`New SOS: ${msg.alert.category}`, { description: msg.alert.message || "Incoming emergency alert" });
          } else if (msg.type === "sos_resolved") {
            setAlerts((prev) => prev.filter((a) => a.id !== msg.alert_id));
          }
        } catch {}
      };
    };
    connect();

    return () => {
      closed = true;
      wsRef.current?.close();
    };
  }, []);

  const respond = async (id) => {
    try {
      await api.post(`/sos/${id}/respond`);
      toast.success("You are now responding");
      load();
    } catch { toast.error("Failed"); }
  };

  const resolve = async (id) => {
    try {
      await api.post(`/sos/${id}/resolve`);
      toast.success("Alert resolved");
      load();
    } catch { toast.error("Failed to resolve"); }
  };

  const points = alerts.map((a) => ({
    lat: a.latitude, lng: a.longitude, label: `${a.category} · ${a.user_name}`, color: CAT_COLOR[a.category] || "destructive",
  }));

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-border/60 glass-nav sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[hsl(var(--destructive))] grid place-items-center sos-pulse">
              <Radio className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-display font-black text-lg tracking-tight leading-none">Responder Portal</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Live SOS · {user?.role}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge className={wsStatus === "live" ? "bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--secondary))]" : "bg-muted text-foreground hover:bg-muted"} data-testid={`ws-status-${wsStatus}`}>
              {wsStatus === "live" ? <Wifi className="w-3 h-3 mr-1.5" /> : <WifiOff className="w-3 h-3 mr-1.5" />}
              {wsStatus.toUpperCase()}
            </Badge>
            <Button variant="outline" size="sm" asChild data-testid="back-to-app">
              <a href="/app">Back to Guardian</a>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="mb-4 flex items-end justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2 font-semibold">Live map</div>
              <div className="font-display font-black text-2xl tracking-tighter">
                {alerts.length} active alert{alerts.length !== 1 ? "s" : ""}
              </div>
            </div>
            <Badge variant="outline" className="text-xs">
              <Users2 className="w-3 h-3 mr-1.5" /> {user?.role} view
            </Badge>
          </div>
          <div className="rounded-2xl overflow-hidden border border-border/70" style={{ height: 500 }}>
            {points.length > 0 ? (
              <LiveMap points={points} zoom={11} showCircles />
            ) : (
              <div className="w-full h-full grid place-items-center bg-muted text-sm text-muted-foreground">
                No active alerts. When someone triggers SOS, it appears here in real time.
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2 font-semibold">Alert queue</div>
          <div className="space-y-3 max-h-[560px] overflow-y-auto pr-1">
            {alerts.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border p-10 text-center">
                <AlertOctagon className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                <div className="font-medium">Queue is empty</div>
                <div className="text-xs text-muted-foreground mt-1">Waiting for incoming SOS...</div>
              </div>
            ) : (
              alerts.map((a, i) => (
                <motion.div
                  key={a.id}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                  className="rounded-2xl border border-border/70 bg-card p-4"
                  data-testid={`responder-alert-${a.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-2.5 h-2.5 rounded-full mt-2 bg-[hsl(var(--${CAT_COLOR[a.category] || "destructive"}))] sos-pulse shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`bg-[hsl(var(--${CAT_COLOR[a.category] || "destructive"}))] hover:bg-[hsl(var(--${CAT_COLOR[a.category] || "destructive"}))] text-white capitalize`}>
                          {a.category}
                        </Badge>
                        <span className="text-xs text-muted-foreground">{new Date(a.created_at).toLocaleTimeString()}</span>
                      </div>
                      <div className="font-semibold">{a.user_name}</div>
                      <div className="text-sm text-muted-foreground line-clamp-2">{a.message || "No message"}</div>
                      <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {a.latitude?.toFixed(4)}, {a.longitude?.toFixed(4)}
                      </div>
                      <div className="flex gap-2 mt-3">
                        {a.user_phone && (
                          <Button asChild size="sm" variant="outline" data-testid={`call-user-${a.id}`}>
                            <a href={`tel:${a.user_phone}`}><Phone className="w-3.5 h-3.5 mr-1.5" /> Call</a>
                          </Button>
                        )}
                        <Button size="sm" onClick={() => respond(a.id)} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white" data-testid={`respond-${a.id}`}>
                          Respond ({a.responder_ids?.length || 0})
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => resolve(a.id)} data-testid={`resolve-responder-${a.id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
